import { PrintProviderError, type PaperWidth, type PrintEvidence, type PrintProvider } from "@/lib/printing";
import type { RestaurantPrintJob, RestaurantPrintRenderModel } from "@/lib/api/types";
import type { StandalonePrintArtifact } from "./renderDocument";

export type PrintJobAck = {
  leaseId: string;
  status: "SENDING" | "SPOOLED" | "FALLBACK_DIALOG_CLOSED" | "FAILED" | "UNKNOWN";
  providerEvidenceCode?: string;
  errorClass?: string;
  errorCode?: string;
  retryable?: boolean;
};

export type CoordinatorJobApi = {
  render(jobId: string, leaseId: string): Promise<RestaurantPrintRenderModel>;
  acknowledge(jobId: string, input: PrintJobAck): Promise<unknown>;
  advanceKitchen(input: { jobId: string; leaseId?: string; commandId: string; ticketId: string; invoiceId: string }): Promise<unknown>;
  completeKitchenAdvance(jobId: string): Promise<unknown>;
};

export type CoordinatorProviders = {
  qz: PrintProvider;
  browser: PrintProvider;
};

export type ProcessJobResult =
  | {
      status: "SPOOLED" | "FALLBACK_DIALOG_CLOSED";
      usedFallback: boolean;
      kitchenAdvanceFailed?: boolean;
    }
  | { status: "FAILED" | "UNKNOWN"; errorClass: string };

const fallbackCodes = new Set(["QZ_CONNECTION_FAILED", "QUEUE_NOT_FOUND", "QUEUE_REQUIRED"]);

function compactError(error: unknown): {
  errorClass: string;
  errorCode?: string;
} {
  if (error instanceof PrintProviderError) {
    return { errorClass: error.code, errorCode: error.code };
  }
  return { errorClass: "PRINT_PREBOUNDARY_FAILED" };
}

function requestFor(model: RestaurantPrintRenderModel, artifact: StandalonePrintArtifact | null) {
  const width: PaperWidth = model.job.paperWidth === "MM58" ? "58mm" : "80mm";
  const drawerCommands: Readonly<Record<string, string>> = {
    "escpos-drawer-pin2": "G3AAGfo=",
    "escpos-drawer-pin5": "G3ABGfo=",
  };
  const drawerCommand = model.job.profileKey ? drawerCommands[model.job.profileKey] : undefined;
  const isDrawer = model.job.kind === "CASH_DRAWER";
  const networkAddress = model.job.provider === "QZ_NETWORK" ? parseNetworkAddress(model.job.providerQueueId) : undefined;
  // Only the raw network path can cut; an OS queue's driver owns its own cut.
  const cut = Boolean(networkAddress) && !isDrawer && model.job.capabilities.includes("CUTTER");
  return {
    ...(artifact ? { documentHtml: artifact.documentHtml } : {}),
    jobName: `${model.job.kind}:${model.job.id}`,
    paperWidth: width,
    copies: model.job.copies,
    profile: {
      id: model.job.profileKey ?? model.job.printerId ?? "browser-fallback",
      paperWidths: [width],
      pixelHtml: !isDrawer,
      ...(drawerCommand ? { rawCommands: { cashDrawerBase64: drawerCommand } } : {}),
    },
    queueName: model.job.providerQueueId ?? undefined,
    ...(networkAddress ? { networkAddress } : {}),
    jobLease: {
      jobId: model.job.id,
      leaseId: model.job.leaseId as string,
    },
    ...(isDrawer ? { hardwareActions: { openCashDrawer: true } } : cut ? { hardwareActions: { cut: true } } : {}),
  } as const;
}

/** The backend stores a network printer as `host:port` (network-printer policy). */
function parseNetworkAddress(stored: string | null): { host: string; port: number } | undefined {
  const match = stored ? /^(\d{1,3}(?:\.\d{1,3}){3}):(\d{1,5})$/.exec(stored) : null;
  return match ? { host: match[1]!, port: Number(match[2]) } : undefined;
}

function usesQz(provider: RestaurantPrintJob["provider"]): boolean {
  return provider === "QZ" || provider === "QZ_NETWORK";
}

export async function processClaimedPrintJob(input: {
  job: RestaurantPrintJob;
  api: CoordinatorJobApi;
  providers: CoordinatorProviders;
  onSendingAcknowledged?(): void;
  renderArtifact(model: RestaurantPrintRenderModel): Promise<StandalonePrintArtifact>;
}): Promise<ProcessJobResult> {
  const leaseId = input.job.leaseId;
  if (!leaseId) {
    return { status: "FAILED", errorClass: "LEASE_MISSING" };
  }

  let crossedBoundary = false;
  let model: RestaurantPrintRenderModel | null = null;
  try {
    model = await input.api.render(input.job.id, leaseId);
    const artifact = model.job.kind === "CASH_DRAWER" ? null : await input.renderArtifact(model);
    const request = requestFor(model, artifact);
    let prepared;
    let usedFallback = !usesQz(model.job.provider);
    if (usesQz(model.job.provider)) {
      try {
        prepared = await input.providers.qz.preflight(request);
      } catch (error) {
        if (!(error instanceof PrintProviderError) || !fallbackCodes.has(error.code)) {
          throw error;
        }
        if (model.job.kind === "CASH_DRAWER") throw error;
        usedFallback = true;
        // The dialog cannot cut: a network printer's raw cut stays with QZ.
        prepared = await input.providers.browser.preflight({
          ...request,
          queueName: undefined,
          networkAddress: undefined,
          hardwareActions: undefined,
          jobLease: undefined,
        });
      }
    } else {
      prepared = await input.providers.browser.preflight({
        ...request,
        queueName: undefined,
        jobLease: undefined,
      });
    }

    const evidence: PrintEvidence = await prepared.send({
      onBeforeDispatch: async () => {
        await input.api.acknowledge(input.job.id, {
          leaseId,
          status: "SENDING",
        });
        crossedBoundary = true;
        input.onSendingAcknowledged?.();
      },
    });

    if (evidence.status === "UNKNOWN") {
      await input.api.acknowledge(input.job.id, {
        leaseId,
        status: "UNKNOWN",
        errorClass: evidence.errorClass ?? "PROVIDER_OUTCOME_UNKNOWN",
      });
      return {
        status: "UNKNOWN",
        errorClass: evidence.errorClass ?? "PROVIDER_OUTCOME_UNKNOWN",
      };
    }

    await input.api.acknowledge(input.job.id, {
      leaseId,
      status: evidence.status,
      providerEvidenceCode: evidence.provider,
    });

    if (model.job.kind === "KITCHEN_CHIT") {
      const command = model.job.preissuedPrintCommand;
      if (!command || command.type !== "printTicket") {
        return {
          status: evidence.status,
          usedFallback,
          kitchenAdvanceFailed: true,
        };
      }
      try {
        await input.api.advanceKitchen({
          jobId: input.job.id,
          leaseId,
          commandId: command.id,
          ticketId: command.payload.ticketId,
          invoiceId: command.invoiceId,
        });
        await input.api.completeKitchenAdvance(input.job.id);
      } catch {
        // The print outcome is already durable. Never turn this into UNKNOWN
        // or print the paper again; the preissued id makes command recovery a
        // separate idempotent operation.
        return {
          status: evidence.status,
          usedFallback,
          kitchenAdvanceFailed: true,
        };
      }
    }
    return { status: evidence.status, usedFallback };
  } catch (error) {
    const compact = compactError(error);
    const status = crossedBoundary ? "UNKNOWN" : "FAILED";
    try {
      await input.api.acknowledge(input.job.id, {
        leaseId,
        status,
        errorClass: compact.errorClass,
        errorCode: compact.errorCode,
        ...(status === "FAILED" ? { retryable: true } : {}),
      });
    } catch {
      // A lost/revoked lease is already durable server state; never replace the
      // original compact failure with a second acknowledgement failure.
    }
    return { status, errorClass: compact.errorClass };
  }
}
